import { Fragment, useEffect, useState } from 'react';
import { type IProduct, type Nullable } from '@recargas-dominicanas/core/types';
import { ProductsApi } from '@recargas-dominicanas/core/api';
import { productCategoryList, productKeyList, productTypeList } from '@recargas-dominicanas/core/constants';
import { Modal } from '../../../components/modal/modal.component';
import { ImageField } from '../../../components/image-field/image-field.component';
import { minLength, required } from '@recargas-dominicanas/core/utils/formValidators';
import { useProductStore } from '../../../store/productStore';
import { useForm } from '@recargas-dominicanas/core/hooks';

import { 
  Autocomplete,
  FieldError,
  Icon,
  OptionIcon,
  Select, 
  SelectOption, 
  TextArea, 
  TextField 
} from '@recargas-dominicanas/core/components';

import { style } from './add-product-modal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AddProductModal(props: Props) {

  const { open, onClose } = props;

  const [priceFieldValue, setPriceFieldValue] = useState('');
  const [disabledButton, setDisabledButton] = useState(false);
  const [productKeys, setProductKeys] = useState(productKeyList);
  const [error, setError] = useState<string>();

  const productStore = useProductStore();

  const form = useForm({
    name: '',
    image: { value: undefined as Nullable<File> },
    key: undefined as Nullable<IProduct['key']>,
    type: undefined as Nullable<IProduct['type']>,
    category: undefined as Nullable<IProduct['category']>,
    profitType: undefined as Nullable<IProduct['profitType']>,
    profit: '',
    min: '',
    max: '',
    instructions: { value: '', optional: true },
    prices: { value: [] as number[], optional: true }
  });

  useEffect(() => {

    if (form.type.value === 'Recarga') {
      form.min.addValidator(required);
      form.max.addValidator(required);
    } else {
      form.min.removeValidator(required);
      form.max.removeValidator(required);
    }

    if (form.type.value === 'Pin') {
      form.instructions.addValidator(required);
      form.prices.addValidator(minLength(1, 'Campo invalido'));
    } else {
      form.instructions.removeValidator(required);
      form.prices.removeValidator(minLength.key);
    }
  }, [open, form.type.value]);

  function handleAddPrice() {
    if (priceFieldValue !== '') {
      const prices = Array.from(new Set([...form.prices.value, parseInt(priceFieldValue)]));
      form.prices.update(prices);
      form.prices.validate(prices);
      setPriceFieldValue('');
    }
  }

  function handleRemovePrice(price: number) {
    form.prices.update(form.prices.value.filter(item => item !== price));
  }

  async function handleSave() {

    if (form.validate()) {

      try {

        setDisabledButton(true);

        await ProductsApi.add({
          name: form.name.value,
          key: form.key.value!, 
          image: form.image.value!,
          type: form.type.value!,
          category: form.category.value!,
          profitType: form.profitType.value!,
          profit: parseFloat(form.profit.value),
          min: form.type.value === 'Recarga' ? parseInt(form.value.min) : undefined,
          max: form.type.value === 'Recarga' ? parseInt(form.value.max) : undefined,
          instructions: form.instructions.value.trim() !== '' ? form.instructions.value : undefined,
          prices: form.prices.value.map(price => ({ price }))
        });

        await productStore.fetch();
  
        setDisabledButton(false);

        handleClose();

      } catch (err) {
        console.error(err);
        setError('Error agregando producto');
        setDisabledButton(false);
      }
    }
  }

  function handleClose() {
    onClose();
    setDisabledButton(false);
    setError(undefined);
    form.clear();
  }

  async function handleSearch(name = '') {
    try {
      setProductKeys(productKeyList.filter(item => item.value.toLowerCase().includes(name.toLowerCase())));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <Modal
      open={open}
      style={style.modal}
      title='Agregar producto'
      error={error}
      secondaryButton='Agregar'
      disabledSecondaryButton={disabledButton}
      onClose={handleClose}
      onSecondaryClick={handleSave}>
      <TextField 
        style={style.textField} 
        label='Nombre'
        value={form.name.value}
        error={form.name.error}
        onChange={form.name.onChange}
        onBlur={form.name.onBlur}/>
      <ImageField
        className={style.imageField}
        label='Logo'
        placeholder='Subir imagen'
        field={form.image}/>
      <Autocomplete
        style={style.autocomplete}
        label='Clave'
        placeholder='Seleccionar clave'
        hideIcon={true}
        value={form.key.value}
        error={form.key.error}
        onChange={form.key.onChange}
        onSearch={handleSearch}>
        {productKeys.map(productKey => (
          <SelectOption
            key={productKey.key} 
            value={productKey.key} 
            label={productKey.value}>
            {productKey.value}
          </SelectOption>
        ))}
      </Autocomplete>
      <Select 
        style={style.select} 
        label={'Tipo'}
        placeholder='Seleccionar tipo'
        value={form.type.value}
        error={form.type.error}
        onChange={(value) => {
          form.type.onChange(value);
          form.type.validate(value);
        }}>
        {productTypeList.map(productType => (
          <SelectOption
            key={productType.key} 
            value={productType.key} 
            label={productType.value}>
            {productType.value}
          </SelectOption>
        ))}
      </Select>
      <Select 
        style={style.select} 
        label={'Categoria'}
        placeholder='Seleccionar categoria'
        value={form.category.value}
        error={form.category.error}
        onChange={(value) => {
          form.category.onChange(value);
          form.category.validate(value);
        }}>
        {productCategoryList.map(productCategory => (
          <SelectOption
            key={productCategory.key} 
            value={productCategory.key} 
            label={productCategory.value}>
            {productCategory.value}
          </SelectOption>
        ))}
      </Select>
      <Select
        style={style.select} 
        label='Tipo de beneficio'
        placeholder='Seleccionar tipo'
        value={form.profitType.value}
        error={form.profitType.error}
        onChange={(value) => {
          form.profitType.onChange(value);
          form.profitType.validate(value);
        }}>
        <SelectOption value='percent' label='Porciento'>Porciento</SelectOption>
        <SelectOption value='fixed' label='Fijo'>Fijo</SelectOption>
      </Select>
      <TextField 
        style={style.textField} 
        label='Beneficio'
        type='number'
        value={form.profit.value}
        error={form.profit.error}
        onChange={form.profit.onChange}
        onBlur={form.profit.onBlur}/>
      {form.value.type === 'Recarga' &&
        <Fragment>
          <TextField 
            style={style.textField} 
            label='Mínimo'
            type='number'
            value={form.min.value}
            error={form.min.error}
            onChange={form.min.onChange}
            onBlur={form.min.onBlur}/>
          <TextField 
            style={style.textField} 
            label='Máximo'
            type='number'
            value={form.max.value}
            error={form.min.error}
            onChange={form.max.onChange}
            onBlur={form.max.onBlur}/>
        </Fragment>
      }
      {form.value.type === 'Pin' &&
        <Fragment>
          <TextArea 
            style={style.textArea} 
            label='Instrucciones'
            value={form.instructions.value}
            error={form.instructions.error}
            onChange={form.instructions.onChange}
            onBlur={form.instructions.onBlur}/>
          <TextField 
            style={style.priceField} 
            type='number'
            label='Precios'
            placeholder='Agregar precio'
            value={priceFieldValue}
            onChange={value => setPriceFieldValue(value)}
            onEnter={handleAddPrice}
            rightIcon={
              <OptionIcon
                className={style.addPriceIcon}
                icon='add' 
                onClick={handleAddPrice}/>
            }/>
          {form.prices.value.length !== 0 &&
            <div className={style.prices}>
              {form.prices.value.map(price => 
                <div 
                  className={style.priceItem} 
                  key={price}>
                  <div className={style.priceValue}>{price}</div>
                  <Icon 
                    className={style.priceDeleteIcon}
                    icon='clear'
                    onClick={() => handleRemovePrice(price)}/>
                </div>
              )}
            </div>
          }
          <FieldError message={form.prices.error}/>
        </Fragment>
      }
    </Modal>
  );
}
